import * as _ from 'lodash';
import { ConfigService } from '../config/config.service';
import { MainService } from '../main/MainService';
import { TitleService } from '../main/title.service';
import { UrlInitParams } from '../main/url_init_params.service';
import { BaseViewController, DisplayNothingEntityVisualiser } from '../views/BaseViewController';
import { IMSVisualiser } from '../views/visualiser';
import { View } from './collections/view';
import { ViewCollection } from './collections/view_collection.service';
import { CachedEventRegistry } from './events/cached_event_registry';
import { ImageUrlService } from 'app/shared/image_url.service';
import { ImsObjectVisibilityService } from 'app/shared/visibility/ImsObjectVisibilityService';
import { AssetCollection } from 'app/shared/collections/asset_collection.service';
import { ZoneCollection } from 'app/shared/collections/zones/zone_collection.service';
import { ImsItemTypeRegistry } from 'app/shared/ims_items/ImsItemTypeRegistry';

/// TODO undo this entire thing
const proxy = new Proxy(
    {},
    {
        get(target: unknown, p: PropertyKey, receiver: unknown): unknown {
            return () => getProxy();
        },
        apply(target: unknown, thisArg: unknown, argumentsList: unknown) {
            return getProxy();
        },
    },
);

function getProxy() {
    return proxy;
}

/*** Use this view when there is an invalid view selected. Nothing will be displayed on the map.
 */
class EmptyViewController extends BaseViewController {
    _assetVisualiser;

    constructor() {
        const mapOptionMock = {
            addOnMapOptionsChanged: () => undefined,
            selectedMapOptions: {},
        } as any;
        super(
            mapOptionMock,
            mapOptionMock,
            proxy as ImageUrlService,
            proxy as ImsObjectVisibilityService,
            proxy as AssetCollection,
            proxy as ZoneCollection,
            proxy as ImsItemTypeRegistry,
        );
        this._assetVisualiser = new IMSVisualiser(
            'none',
            'none',
            'None',
            null,
            null,
            new DisplayNothingEntityVisualiser(),
            [],
        );
    }

    get currentAssetVisualiser() {
        return this._assetVisualiser;
    }

    getAssetVisualiserForCurrentMapOptions() {
        return this._assetVisualiser;
    }
}

interface ViewControllerStorage {
    [name: string]: BaseViewController;
}

interface ReturnedViews {
    viewFromUrlInitParams: View;
    viewFromLocalStorage: View;
    viewFromConfigService: View;
    viewFromFirstViewInCollection: View;
}

interface ViewStateServiceInterface {
    loadInitialViewAndController: () => Promise<void>;
    setSelectedView: (imsView: View) => void;
    setViewController: (name: string, viewObject: BaseViewController) => void;
    selectedView: View;
    getCurrentView: () => View;
    getViewFromViewName: (name: string) => void;
    onInitialViewLoad: (callback: (viewStateService: ViewStateService) => any) => void;
    onViewSelectionChanged: (callback: (viewStateService: ViewStateService) => any) => void;
    currentViewController: BaseViewController;
    allViewControllers: ViewControllerStorage;
}

export class ViewStateService implements ViewStateServiceInterface {
    private _initParams;
    private _localStorage;
    private _titleService: TitleService;
    private _viewCollection: ViewCollection;
    private _mainService: MainService;
    private _configService: ConfigService;
    private _viewSelectionChangedEventRegistry: CachedEventRegistry;
    private _initialViewSet: CachedEventRegistry;
    private _viewControllers: ViewControllerStorage;
    private _selectedView: View;

    // Return this empty view as the current view when there is no view selected. This avoids any
    // races as the current view is always valid
    _emptyViewController: EmptyViewController;

    constructor(
        initParams: UrlInitParams,
        localStorageService: WindowLocalStorage,
        titleService: TitleService,
        viewCollection: ViewCollection,
        mainService: MainService,
        configService: ConfigService,
    ) {
        this._initParams = initParams;
        this._localStorage = localStorageService;
        this._titleService = titleService;
        this._viewCollection = viewCollection;
        this._mainService = mainService;
        this._configService = configService;
        this._viewSelectionChangedEventRegistry = new CachedEventRegistry();
        this._initialViewSet = new CachedEventRegistry();

        // Return this empty view as the current view when there is no view selected. This avoids any
        // races as the current view is always valid
        this._emptyViewController = new EmptyViewController();

        this._selectedView = null;

        this._viewControllers = {};
    }

    private getInitialView(): View {
        const allViewsFromViewCollection = this._viewCollection.getAllAsArray();
        if (!allViewsFromViewCollection.length) {
            console.error('no views found in collection!');
        }

        const {
            viewFromUrlInitParams,
            viewFromLocalStorage,
            viewFromConfigService,
            viewFromFirstViewInCollection,
        } = this._findAllPossibleViewsForInitialisation(allViewsFromViewCollection);

        if (viewFromUrlInitParams) {
            return viewFromUrlInitParams;
        } else if (viewFromLocalStorage) {
            return viewFromLocalStorage;
        } else if (viewFromConfigService) {
            return viewFromConfigService;
        }

        return viewFromFirstViewInCollection;
    }

    async loadInitialViewAndController(): Promise<void> {
        const view = this.getInitialView();
        await this.setSelectedView(view);
        await this._initialViewSet.notifyListeners();
    }

    private _findAllPossibleViewsForInitialisation(
        allViewsFromViewCollection: Array<View>,
    ): ReturnedViews {
        const initParamsViewValue = this._initParams.view ? this._initParams.view.value : null;
        const viewFromUrlInitParams = this.getViewFromViewName(initParamsViewValue);
        const viewFromLocalStorage = this.getViewFromViewName(
            this._localStorage.get('ViewState:SelectedViewType'),
        );
        const viewFromFirstViewInCollection = allViewsFromViewCollection[0];
        const viewFromConfigService = allViewsFromViewCollection.find(
            (view) => view.name === this._configService.defaultViewName,
        );
        return {
            viewFromUrlInitParams,
            viewFromLocalStorage,
            viewFromFirstViewInCollection,
            viewFromConfigService,
        };
    }

    public async setSelectedView(imsView: View): Promise<void> {
        if (!imsView || !this._viewControllers[imsView.name]) {
            console.error(`requested selected view is invalid`, imsView, this._viewControllers);
            return;
        }

        this._selectedView = imsView;
        this._startNewView(this._selectedView);
        this._localStorage.set('ViewState:SelectedViewType', this._selectedView.name);
        await this._viewSelectionChangedEventRegistry.notifyListeners(this);
    }

    public getViewFromViewName(name: string): View {
        return _.find(this._viewCollection.getAllAsArray(), (s) => s.name === name);
    }

    private _startNewView(view: View) {
        if (!view) {
            throw new Error('View not found. Can not start a new view');
        }
        // stop every view then start the one we want
        _.forEach(this._viewControllers, (v) => v.stopView());
        this._viewControllers[view.name].startView(view);
    }

    get currentViewController(): BaseViewController {
        return this._selectedView
            ? this._viewControllers[this._selectedView.name]
            : this._emptyViewController;
    }

    get assetVisualiserForCurrentMapOptions() {
        return this._selectedView
            ? this._viewControllers[
                  this._selectedView.name
              ].getAssetVisualiserForCurrentMapOptions()
            : this._emptyViewController.getAssetVisualiserForCurrentMapOptions();
    }

    get currentViewName(): string {
        return _.findKey(this._viewControllers, (view) => view === this.currentViewController);
    }

    get allViewControllers(): ViewControllerStorage {
        return this._viewControllers;
    }

    set allViewControllers(viewControllers: ViewControllerStorage) {
        this._viewControllers = viewControllers;
    }

    public getCurrentView(): View {
        return this.currentViewController.getView();
    }

    public getViewController(name: string): BaseViewController {
        return this._viewControllers[name];
    }

    public setViewController(name: string, viewObject: BaseViewController) {
        this._viewControllers[name] = viewObject;
    }

    get selectedView() {
        return this._selectedView;
    }

    public onViewSelectionChanged(
        callback: (viewStateService: ViewStateService) => any,
    ): () => void {
        return this._viewSelectionChangedEventRegistry.addEventListener(callback);
    }

    public onInitialViewLoad(callback: (viewStateService: ViewStateService) => any): () => void {
        return this._initialViewSet.addEventListener(callback);
    }
}
